// src/pages/conversations/ConversationSidebarList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, List, ListItemButton, ListItemText, Typography,
  CircularProgress, Paper, TextField, InputAdornment, Pagination, Tooltip, Divider, Stack
} from '@mui/material';
import { Search as SearchIcon, ChatBubbleOutline as ConversationIcon } from '@mui/icons-material';
import { conversationService } from '../../api/conversationService';
import { getConversationPreview, formatDisplayDate } from '../../utils/conversationUtils';
import { useDebounce } from '../../hooks/useDebounce';

const ConversationSidebarList = ({ currentConversationId, initialListParams }) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for the list, initialized by initialListParams or defaults
  const [page, setPage] = useState(initialListParams?.page || 0); // 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(initialListParams?.rowsPerPage || 15); // Sidebar might use more items
  const [totalConversations, setTotalConversations] = useState(initialListParams?.totalConversations || 0);
  const [searchTerm, setSearchTerm] = useState(initialListParams?.searchTerm || '');
  const [agentIdFilter, setAgentIdFilter] = useState(initialListParams?.agentIdForFilter || null);
  const [sortBy, setSortBy] = useState(initialListParams?.sortBy || 'updated_at');
  const [sortOrder, setSortOrder] = useState(initialListParams?.sortOrder || 'desc');

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchConversationsForSidebar = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await conversationService.listConversations(
      agentIdFilter,
      rowsPerPage,
      page * rowsPerPage,
      debouncedSearchTerm || null,
      sortBy,
      sortOrder
    );
    if (res.isError) {
      setError(res.message || 'Failed to load conversations.');
      setConversations([]);
      setTotalConversations(0);
    } else {
      setConversations(Array.isArray(res.items) ? res.items : []);
      setTotalConversations(res.total || 0);
    }
    setLoading(false);
  }, [agentIdFilter, page, rowsPerPage, debouncedSearchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchConversationsForSidebar();
  }, [fetchConversationsForSidebar]);

  // Effect to update internal state if initialListParams change (e.g., user navigates from main list with different filters)
  // This is important if the user goes "Back" to ConversationList, changes filters, and comes back to a Detail view.
  useEffect(() => {
    if (initialListParams) {
        setPage(initialListParams.page || 0);
        setRowsPerPage(initialListParams.rowsPerPage || 15);
        setTotalConversations(initialListParams.totalConversations || 0);
        setSearchTerm(initialListParams.searchTerm || '');
        setAgentIdFilter(initialListParams.agentIdForFilter || null);
        setSortBy(initialListParams.sortBy || 'updated_at');
        setSortOrder(initialListParams.sortOrder || 'desc');
    }
  }, [initialListParams]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1); // MUI Pagination is 1-based, API is 0-based
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset page on new search
  };

  // This object will be passed in location.state when navigating
  const getCurrentSidebarListParams = () => ({
    page, rowsPerPage, searchTerm: debouncedSearchTerm, agentIdForFilter: agentIdFilter,
    sortBy, sortOrder, totalConversations,
  });

  const handleSelectConversation = (selectedConvId) => {
    navigate(`/conversations/${selectedConvId}`, {
      state: { listParams: getCurrentSidebarListParams() }
    });
  };

  return (
    <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, pb:1, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
          Conversations
        </Typography>
        {/* Placeholder for potential future actions like "New Conversation" or global filters for sidebar */}
      </Stack>
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search in list..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
      </Box>
      {loading && conversations.length === 0 && <Box sx={{textAlign: 'center', py:3}}><CircularProgress size={30}/></Box>}
      {error && <Typography color="error" sx={{p:1.5, textAlign:'center'}}>{error}</Typography>}
      
      <List sx={{ overflowY: 'auto', flexGrow: 1, p:0, pt:0.5, '& .MuiListItemButton-root': { pt: 0.75, pb: 0.75, pr: 1, pl: 1.5 } }}>
        {!loading && conversations.length === 0 && !error && (
          <ListItemText primary="No conversations found." sx={{textAlign:'center', p:2, color: 'text.secondary'}}/>
        )}
        {conversations.map(conv => (
          <ListItemButton
            key={conv.id}
            selected={conv.id === currentConversationId}
            onClick={() => handleSelectConversation(conv.id)}
            sx={{ 
                flexDirection: 'column', 
                alignItems: 'flex-start', 
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                '&.Mui-selected': {
                    bgcolor: 'action.selected',
                    '&:hover': {
                        bgcolor: 'action.selected', // Keep selected color on hover
                    }
                },
                '&:last-child': {
                    borderBottom: 'none'
                }
            }}
          >
            <Tooltip title={getConversationPreview(conv)} placement="right" enterDelay={700}>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{width: '100%'}}>
                        <ConversationIcon sx={{fontSize: '1rem', color: conv.id === currentConversationId ? 'primary.main' : 'text.secondary', flexShrink: 0}} />
                        <Typography 
                            variant="body2" 
                            noWrap 
                            sx={{
                                fontWeight: conv.id === currentConversationId ? 600 : 400, 
                                width: '100%',
                                color: conv.id === currentConversationId ? 'primary.main' : 'text.primary',
                            }}
                        >
                            {getConversationPreview(conv)}
                        </Typography>
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" noWrap sx={{pl: 3.25, pt:0.25}}>
                        {`Updated: ${formatDisplayDate(conv.updated_at)}`}
                    </Typography>
                  }
                />
            </Tooltip>
          </ListItemButton>
        ))}
      </List>
      {!loading && totalConversations > 0 && conversations.length > 0 && (
         <Pagination
            count={Math.ceil(totalConversations / rowsPerPage)}
            page={page + 1} // MUI Pagination is 1-based
            onChange={handlePageChange}
            size="small"
            variant="outlined"
            shape="rounded"
            sx={{ display: 'flex', justifyContent: 'center', p: 1.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
          />
      )}
    </Paper>
  );
};

export default ConversationSidebarList;